import React, {useEffect, useState, useRef} from "react";
import {BeatLoader} from "react-spinners";
import {toast} from "react-toastify";
import {useStateValue} from "../../../states/StateProvider";
import {AES, enc} from "crypto-js";
import {useNavigate} from "react-router";
import {documentAPI, generationAPI, initializeiFExAuth} from "../../../Api/ifex";
import {FiDownload, FiCheck, FiAlertCircle, FiX, FiFile, FiFilter, FiEye, FiCode, FiChevronDown, FiChevronUp} from "react-icons/fi";
import PdfPreviewModal from "../../../components/modal/PdfPreviewModal";
import Api from "../../../Api/api";

const DocumentGeneration = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [{secretKey, portal}, dispatch] = useStateValue();
    const navigate = useNavigate();

    // Tab state
    const [activeTab, setActiveTab] = useState('generate'); // 'generate' | 'history'

    // Documents state
    const [documents, setDocuments] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);

    // Form state
    const [customerName, setCustomerName] = useState('');
    const [customerReference, setCustomerReference] = useState('');
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [password, setPassword] = useState('');
    const [generating, setGenerating] = useState(false);

    // Company autocomplete state
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [companySearchTerm, setCompanySearchTerm] = useState('');
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
    const [companiesLoading, setCompaniesLoading] = useState(false);
    const companyInputRef = useRef(null);

    // History state
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [perPage] = useState(15);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');

    // Result modal
    const [showResultModal, setShowResultModal] = useState(false);
    const [generationResult, setGenerationResult] = useState(null);

    // PDF Preview modal
    const [showPdfPreview, setShowPdfPreview] = useState(false);
    const [previewPdfUrl, setPreviewPdfUrl] = useState('');
    const [previewFileName, setPreviewFileName] = useState('');

    // Debug mode state
    const [expandedDebugSections, setExpandedDebugSections] = useState({});

    useEffect(() => {
        try {
            const decryptedBytes = localStorage.getItem('user') ? AES.decrypt(localStorage.getItem('user'), secretKey) : false;
            const userData = JSON.parse(decryptedBytes.toString(enc.Utf8));

            if (!userData) {
                navigate('/anmeldung');
                return;
            }

            setUser(userData);
            
            // Initialize iFEx authentication
            initializeiFExAuth().then(() => {
                setLoading(false);
            }).catch(error => {
                console.error('iFEx authentication failed:', error);
                setLoading(false);
            });
        } catch (e) {
            console.error('Authentication error:', e);
            window.location.replace('/anmeldung');
        }
    }, [secretKey, navigate]);

    useEffect(() => {
        if (user) {
            fetchDocuments();
            fetchCompanies();
        }
    }, [user]);

    useEffect(() => {
        if (user && activeTab === 'history') {
            fetchHistory();
        }
    }, [user, activeTab, currentPage, searchTerm, filterStatus, filterDateFrom, filterDateTo]);

    // Click outside handler for company dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (companyInputRef.current && !companyInputRef.current.contains(event.target)) {
                setShowCompanyDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const fetchDocuments = async () => {
        setDocumentsLoading(true);
        try {
            const response = await documentAPI.getAll({ is_active: 1, per_page: 100 });
            if (response.data.success) {
                setDocuments(response.data.data.data || response.data.data);
            }
        } catch (error) {
            console.error('Error fetching documents:', error);
            toast.error('Fehler beim Laden der Dokumente');
        } finally {
            setDocumentsLoading(false);
        }
    };

    const fetchCompanies = async (searchQuery = '') => {
        setCompaniesLoading(true);
        try {
            const formData = new FormData();
            formData.append('portal', 'dgg');
            formData.append('userID', user?.id || 1);
            formData.append('rows', '10000'); // Fetch all companies
            formData.append('page', '1');
            formData.append('sortColumn', '7');
            formData.append('sortMethod', 'asc');
            formData.append('filterID', JSON.stringify({
                a: null,
                b: null,
                c: null,
                d: null,
                e: null,
                f: null,
                g: null,
                h: 111,
                i: null,
                j: null,
                k: 114,
                l: null,
                m: null
            }));
            formData.append('filter', JSON.stringify({
                a: null,
                b: null,
                c: null,
                d: null,
                e: null,
                f: null,
                g: null,
                h: 1,
                i: 0,
                j: 0,
                k: 1,
                l: 0,
                m: 0
            }));
            formData.append('dateFilter', JSON.stringify({
                id: null,
                value: null
            }));

            const response = await Api().post('/getBestands', formData);

            // The API returns {bestands: Array(...)}
            const companiesData = response.data.bestands || response.data;

            if (companiesData && Array.isArray(companiesData)) {
                setCompanies(companiesData);
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
            toast.error('Fehler beim Laden der Firmen');
        } finally {
            setCompaniesLoading(false);
        }
    };

    const fetchHistory = async () => {
        setHistoryLoading(true);
        try {
            const params = {
                page: currentPage,
                per_page: perPage,
                ...(searchTerm && { search: searchTerm }),
                ...(filterStatus && { status: filterStatus }),
                ...(filterDateFrom && { date_from: filterDateFrom }),
                ...(filterDateTo && { date_to: filterDateTo }),
            };

            const response = await generationAPI.getHistory(params);

            if (response.data.success) {
                // Response structure: { success: true, data: [...], meta: {...} }
                const historyData = response.data.data || [];
                setHistory(historyData);
                setTotalPages(response.data.meta?.last_page || 1);
            }
        } catch (error) {
            console.error('Error fetching history:', error);
            toast.error('Fehler beim Laden der Historie');
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleCompanySelect = (company) => {
        setSelectedCompany(company);
        setCustomerName(company.FirmaKurz || company.Firma || '');
        setCustomerReference(company.FP_ID?.toString() || '');
        setCompanySearchTerm(company.FirmaKurz || company.Firma || '');
        setShowCompanyDropdown(false);
    };

    const handleCompanyInputChange = (e) => {
        const value = e.target.value;
        setCompanySearchTerm(value);
        setShowCompanyDropdown(true); // Always show dropdown when typing

        // Clear selection if user types
        if (selectedCompany) {
            setSelectedCompany(null);
            setCustomerName('');
            setCustomerReference('');
        }
    };

    const handleCompanyInputFocus = () => {
        setShowCompanyDropdown(true); // Show dropdown on focus
    };

    const handleDocumentToggle = (docId) => {
        setSelectedDocuments(prev =>
            prev.includes(docId)
                ? prev.filter(id => id !== docId)
                : [...prev, docId]
        );
    };

    const handleSelectAll = (module) => {
        const moduleDocIds = documents
            .filter(doc => doc.module === module)
            .map(doc => doc.id);

        const allSelected = moduleDocIds.every(id => selectedDocuments.includes(id));

        if (allSelected) {
            // Deselect all from this module
            setSelectedDocuments(prev => prev.filter(id => !moduleDocIds.includes(id)));
        } else {
            // Select all from this module
            setSelectedDocuments(prev => [...new Set([...prev, ...moduleDocIds])]);
        }
    };

    const handleGenerate = async () => {
        // Validation
        if (!selectedCompany) {
            toast.error('Bitte wählen Sie eine Firma aus der Liste aus');
            return;
        }

        if (!customerReference.trim()) {
            toast.error('Kundenreferenz (FP_ID) ist erforderlich');
            return;
        }

        if (selectedDocuments.length === 0) {
            toast.error('Bitte wählen Sie mindestens ein Dokument aus');
            return;
        }

        setGenerating(true);
        try {
            const data = {
                document_ids: selectedDocuments,
                customer_name: customerName.trim(),
                customer_reference: customerReference.trim() || null,
                output_format: 'pdf',
                password: password.trim() || null,
                merge_pdfs: true,
                merge_by_sequence: true,
                generated_by: user.name || user.email,
            };

            const response = await generationAPI.generateBatch(data);

            // Check if response has data array with results
            if (response.data && response.data.data && response.data.data.length > 0) {
                setGenerationResult({
                    success: true,
                    ...response.data
                });
                setShowResultModal(true);
                toast.success('Dokumente erfolgreich generiert!');

                // Reset form
                setCustomerName('');
                setCustomerReference('');
                setSelectedCompany(null);
                setCompanySearchTerm('');
                setSelectedDocuments([]);
                setPassword('');
            }
        } catch (error) {
            console.error('Error generating documents:', error);

            // Extract all error information from response
            const errorData = error.response?.data || {};
            const hasDetailedErrors = errorData.errors || errorData.error || errorData.message;

            if (hasDetailedErrors) {
                // Collect all error messages
                const allErrors = [];

                // Add general error message
                if (errorData.error) {
                    allErrors.push(errorData.error);
                }
                if (errorData.message && errorData.message !== errorData.error) {
                    allErrors.push(errorData.message);
                }

                // Add validation errors (object with field names as keys)
                if (errorData.errors && typeof errorData.errors === 'object') {
                    Object.entries(errorData.errors).forEach(([field, messages]) => {
                        if (Array.isArray(messages)) {
                            messages.forEach(msg => allErrors.push(`${field}: ${msg}`));
                        } else if (typeof messages === 'string') {
                            allErrors.push(`${field}: ${messages}`);
                        }
                    });
                }

                // Add missing placeholder information
                const missingPlaceholders = errorData.missing_placeholders || [];
                if (missingPlaceholders.length > 0) {
                    allErrors.push(`Fehlende Platzhalter: ${missingPlaceholders.join(', ')}`);
                }

                // Show modal with detailed errors
                setGenerationResult({
                    success: false,
                    errors: errorData.errors,
                    error: errorData.error,
                    message: errorData.message,
                    missing_placeholders: missingPlaceholders,
                    allErrorMessages: allErrors
                });
                setShowResultModal(true);

                // Also show toast with first few errors for immediate feedback
                if (allErrors.length > 0) {
                    const errorPreview = allErrors.slice(0, 3).join('\n');
                    const moreErrors = allErrors.length > 3 ? `\n...und ${allErrors.length - 3} weitere Fehler` : '';
                    toast.error(
                        <div className="whitespace-pre-line">
                            <strong>Generierung fehlgeschlagen:</strong>
                            {'\n' + errorPreview + moreErrors}
                        </div>,
                        { autoClose: 8000 }
                    );
                } else {
                    toast.error('Fehler beim Generieren der Dokumente. Details siehe Modal.');
                }
            } else {
                // Generic error without details
                const errorMessage = error.message || 'Unbekannter Fehler';
                toast.error(
                    <div>
                        <strong>Fehler beim Generieren der Dokumente</strong>
                        <br />
                        {errorMessage}
                    </div>
                );
            }
        } finally {
            setGenerating(false);
        }
    };

    const handleDownload = async (generationId, format) => {
        try {
            const response = await generationAPI.download(generationId, format);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `document.${format}`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Download gestartet');
        } catch (error) {
            console.error('Error downloading:', error);
            toast.error('Fehler beim Herunterladen');
        }
    };

    const handleCloseResultModal = () => {
        setShowResultModal(false);
        setGenerationResult(null);
        setExpandedDebugSections({});

        // Refresh history if on history tab
        if (activeTab === 'history') {
            fetchHistory();
        }
    };

    const toggleDebugSection = (docId) => {
        setExpandedDebugSections(prev => ({
            ...prev,
            [docId]: !prev[docId]
        }));
    };

    const handlePreviewPdf = (url, fileName) => {
        setPreviewPdfUrl(url);
        setPreviewFileName(fileName);
        setShowPdfPreview(true);
    };

    const handleClosePdfPreview = () => {
        setShowPdfPreview(false);
        setPreviewPdfUrl('');
        setPreviewFileName('');
    };

    const groupDocumentsByModule = () => {
        const grouped = {};
        documents.forEach(doc => {
            if (!grouped[doc.module]) {
                grouped[doc.module] = [];
            }
            grouped[doc.module].push(doc);
        });

        // Sort by sequence within each module
        Object.keys(grouped).forEach(module => {
            grouped[module].sort((a, b) => {
                const seqA = a.sequence || '';
                const seqB = b.sequence || '';
                return seqA.localeCompare(seqB, undefined, { numeric: true });
            });
        });

        return grouped;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('de-DE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Filter companies based on search term - search across all text fields
    const filteredCompanies = React.useMemo(() => {
        if (!companySearchTerm || companySearchTerm.trim() === '') {
            return companies.slice(0, 100);
        }

        const searchLower = companySearchTerm.toLowerCase().trim();

        const filtered = companies.filter(company => {
            // Get all string values from the company object
            const allValues = Object.values(company)
                .filter(val => val !== null && val !== undefined)
                .map(val => val.toString().toLowerCase());

            return allValues.some(value => value.includes(searchLower));
        }).slice(0, 100);

        return filtered;
    }, [companies, companySearchTerm]);

    if (loading) {
        return (
            <div className='dashboardContainer flex justify-center items-center'>
                <BeatLoader color="#1e3a8a" size={15} />
            </div>
        );
    }

    const groupedDocuments = groupDocumentsByModule();

    return (
        <div className='dashboardContainer'>
            <div className='flex justify-between items-center content-center pb-5'>
                <h2 className='text-2xl lg:text-left'>Dokumente Generieren</h2>
            </div>

            {/* Tabs */}
            <div className="mb-6 border-b border-gray-200">
                <div className="flex gap-4">
                    <button
                        onClick={() => setActiveTab('generate')}
                        className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                            activeTab === 'generate'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        Generieren
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                            activeTab === 'history'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        Historie
                    </button>
                </div>
            </div>

            {/* Generate Tab */}
            {activeTab === 'generate' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Customer & Settings */}
                    <div className="lg:col-span-1 space-y-6">
                        {/* Customer Information */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Kundeninformationen</h3>

                            <div className="mb-4" ref={companyInputRef}>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Firma suchen *
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            selectedCompany ? 'border-green-500 bg-green-50' : 'border-gray-300'
                                        }`}
                                        placeholder="Firmenname eingeben..."
                                        value={companySearchTerm}
                                        onChange={handleCompanyInputChange}
                                        onFocus={handleCompanyInputFocus}
                                        autoComplete="off"
                                    />
                                    {companiesLoading && (
                                        <div className="absolute right-3 top-3">
                                            <BeatLoader size={6} color="#1e3a8a" />
                                        </div>
                                    )}
                                    {selectedCompany && (
                                        <div className="absolute right-3 top-3 text-green-600">
                                            <FiCheck size={18} />
                                        </div>
                                    )}

                                    {/* Dropdown */}
                                    {showCompanyDropdown && !companiesLoading && companies.length > 0 && (
                                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                                            {filteredCompanies.length > 0 ? (
                                                <>
                                                    <div className="sticky top-0 bg-gray-50 px-3 py-2 border-b border-gray-200 text-xs text-gray-600">
                                                        {filteredCompanies.length} {filteredCompanies.length === 100 ? '+ ' : ''}Firma{filteredCompanies.length !== 1 ? 'en' : ''} gefunden
                                                    </div>
                                                    {filteredCompanies.map((company, index) => (
                                                        <div
                                                            key={company.FP_ID || index}
                                                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                                            onClick={() => handleCompanySelect(company)}
                                                        >
                                                            <div className="font-medium text-gray-800">
                                                                {company.FirmaKurz || company.Firma}
                                                            </div>
                                                            <div className="text-xs text-gray-500 flex justify-between">
                                                                <span>{company.Firma}</span>
                                                                <span className="text-blue-600">FP: {company.FP_ID}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </>
                                            ) : (
                                                <div className="px-3 py-2 text-gray-500 text-sm">
                                                    Keine Firma gefunden für "{companySearchTerm}"
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {selectedCompany && (
                                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                        <FiCheck size={12} />
                                        Ausgewählt: {selectedCompany.FirmaKurz} (FP: {selectedCompany.FP_ID})
                                    </p>
                                )}
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    FP-ID (Kundenreferenz) *
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
                                    placeholder="Wird automatisch gesetzt"
                                    value={customerReference}
                                    readOnly
                                    disabled
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    FP-ID wird automatisch aus der ausgewählten Firma übernommen
                                </p>
                            </div>
                        </div>

                        {/* Output Settings */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Ausgabeeinstellungen</h3>

                            <div className="mb-4">
                                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                    <FiFile className="text-blue-600" size={20} />
                                    <div>
                                        <span className="text-sm font-semibold text-blue-800">Format: PDF</span>
                                        <p className="text-xs text-blue-600 mt-0.5">
                                            DOCX-Vorlagen mit LibreOffice zu PDF konvertiert (100% genau)
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Passwortschutz (optional)
                                </label>
                                <input
                                    type="password"
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Passwort für PDF-Schutz eingeben"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="new-password"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Leer lassen für PDF ohne Passwortschutz
                                </p>
                            </div>
                        </div>

                        {/* Generate Button */}
                        <button
                            onClick={handleGenerate}
                            disabled={generating || selectedDocuments.length === 0 || !selectedCompany || !customerReference.trim()}
                            className={`w-full py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2 ${
                                generating || selectedDocuments.length === 0 || !selectedCompany || !customerReference.trim()
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-mainBlue hover:bg-opacity-90'
                            }`}
                        >
                            {generating ? (
                                <>
                                    <BeatLoader size={8} color="#ffffff" />
                                    <span>Generiere...</span>
                                </>
                            ) : (
                                <>
                                    <FiDownload />
                                    <span>Dokumente Generieren</span>
                                </>
                            )}
                        </button>

                        {selectedDocuments.length > 0 && (
                            <div className="text-sm text-gray-600 text-center">
                                {selectedDocuments.length} Dokument{selectedDocuments.length !== 1 ? 'e' : ''} ausgewählt
                            </div>
                        )}
                    </div>

                    {/* Right Column - Document Selection */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Dokumentauswahl</h3>

                            {documentsLoading ? (
                                <div className="text-center py-8">
                                    <BeatLoader color="#1e3a8a" size={10} />
                                </div>
                            ) : Object.keys(groupedDocuments).length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <FiAlertCircle className="mx-auto mb-2" size={24} />
                                    <p>Keine aktiven Dokumente verfügbar</p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {Object.entries(groupedDocuments).map(([module, docs]) => (
                                        <div key={module} className="border border-gray-200 rounded-lg p-4">
                                            <div className="flex justify-between items-center mb-3">
                                                <h4 className="font-semibold text-gray-700 uppercase text-sm">
                                                    {module}
                                                </h4>
                                                <button
                                                    onClick={() => handleSelectAll(module)}
                                                    className="text-sm text-blue-600 hover:text-blue-800"
                                                >
                                                    {docs.every(doc => selectedDocuments.includes(doc.id))
                                                        ? 'Alle abwählen'
                                                        : 'Alle auswählen'}
                                                </button>
                                            </div>
                                            <div className="space-y-2">
                                                {docs.map(doc => (
                                                    <label
                                                        key={doc.id}
                                                        className="flex items-start p-2 hover:bg-gray-50 rounded cursor-pointer"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedDocuments.includes(doc.id)}
                                                            onChange={() => handleDocumentToggle(doc.id)}
                                                            className="mt-1 mr-3"
                                                        />
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-medium text-gray-800">
                                                                    {doc.document_name}
                                                                </span>
                                                                {doc.sequence && (
                                                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                                                        Seq: {doc.sequence}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {doc.remarks && (
                                                                <p className="text-xs text-gray-500 mt-1">
                                                                    {doc.remarks}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
                <div className="bg-white rounded-lg shadow">
                    {/* Filters */}
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center gap-2 mb-4">
                            <FiFilter className="text-gray-500" />
                            <h3 className="font-semibold text-gray-700">Filter</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <input
                                type="text"
                                placeholder="Suche nach Dateiname oder Kunde..."
                                className="px-3 py-2 border border-gray-300 rounded text-sm"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                            <select
                                className="px-3 py-2 border border-gray-300 rounded text-sm"
                                value={filterStatus}
                                onChange={(e) => {
                                    setFilterStatus(e.target.value);
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="">Alle Status</option>
                                <option value="completed">Abgeschlossen</option>
                                <option value="failed">Fehlgeschlagen</option>
                            </select>
                            <input
                                type="date"
                                className="px-3 py-2 border border-gray-300 rounded text-sm"
                                value={filterDateFrom}
                                onChange={(e) => {
                                    setFilterDateFrom(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                            <input
                                type="date"
                                className="px-3 py-2 border border-gray-300 rounded text-sm"
                                value={filterDateTo}
                                onChange={(e) => {
                                    setFilterDateTo(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        {historyLoading ? (
                            <div className="text-center py-8">
                                <BeatLoader color="#1e3a8a" size={10} />
                            </div>
                        ) : history.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <p>Keine Generierungen gefunden</p>
                            </div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Dateiname
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Kunde
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Referenz
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Format
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Erstellt von
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Erstellt am
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Aktionen
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {history.map((item) => (
                                        <tr key={item.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {item.generated_filename || item.document_name || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {item.customer_reference || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {item.customer_reference || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <span className="uppercase font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                                                    {item.output_format || '-'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {item.generated_by || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {formatDate(item.generated_at)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {item.status === 'success' || item.status === 'completed' ? (
                                                    <span className="px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800">
                                                        Abgeschlossen
                                                    </span>
                                                ) : (
                                                    <span className="px-2 py-1 text-xs font-semibold rounded bg-red-100 text-red-800">
                                                        Fehlgeschlagen
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {(item.status === 'success' || item.status === 'completed') && (
                                                    <div className="flex gap-2">
                                                        {(item.output_format === 'docx' || item.output_format === 'both') && (
                                                            <button
                                                                onClick={() => handleDownload(item.id, 'docx')}
                                                                className="text-blue-600 hover:text-blue-800"
                                                                title="Word herunterladen"
                                                            >
                                                                <FiDownload size={18} />
                                                            </button>
                                                        )}
                                                        {(item.output_format === 'pdf' || item.output_format === 'both') && (
                                                            <>
                                                                <button
                                                                    onClick={() => {
                                                                        const baseUrl = process.env.REACT_APP_IFEX_API_URL || 'http://localhost:8002/api';
                                                                        const apiUrl = `${baseUrl}/generations/${item.id}/download?format=pdf`;
                                                                        handlePreviewPdf(apiUrl, item.generated_filename || 'document.pdf');
                                                                    }}
                                                                    className="text-mainBlue hover:text-blue-800"
                                                                    title="PDF Vorschau"
                                                                >
                                                                    <FiEye size={18} />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDownload(item.id, 'pdf')}
                                                                    className="text-red-600 hover:text-red-800"
                                                                    title="PDF herunterladen"
                                                                >
                                                                    <FiFile size={18} />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                            <div className="text-sm text-gray-500">
                                Seite {currentPage} von {totalPages}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                    Zurück
                                </button>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                    Weiter
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Result Modal */}
            {showResultModal && generationResult && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-modalBackDropColor bg-opacity-60"
                        onClick={handleCloseResultModal}
                    ></div>
                    <div className="relative z-10 bg-white rounded-xl shadow-2xl w-11/12 max-w-2xl max-h-[90vh] overflow-y-auto">
                        {/* Header */}
                        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
                            <h2 className="text-2xl font-bold text-gray-800">
                                {generationResult.success ? 'Generierung Erfolgreich' : 'Generierung Fehlgeschlagen'}
                            </h2>
                            <button onClick={handleCloseResultModal} className="text-gray-500 hover:text-gray-700">
                                <FiX size={24} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                            {generationResult.success ? (
                                <div>
                                    <div className="mb-4 flex justify-center">
                                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                                            <FiCheck className="text-green-600" size={32} />
                                        </div>
                                    </div>
                                    <p className="text-center text-gray-700 mb-4">
                                        Ihre Dokumente wurden erfolgreich generiert und stehen zum Download bereit.
                                    </p>
                                    <div className="bg-gray-50 p-4 rounded space-y-4">
                                        {/* Display each generated document */}
                                        {generationResult.data && generationResult.data.map((doc, idx) => (
                                            <div key={idx} className="bg-white p-4 rounded border border-gray-200">
                                                <div className="flex items-start justify-between mb-3">
                                                    <div>
                                                        <h4 className="font-semibold text-gray-800">{doc.document_name}</h4>
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            Generiert: {new Date(doc.generated_at).toLocaleString('de-DE')}
                                                        </p>
                                                    </div>
                                                    <span className={`px-2 py-1 text-xs font-semibold rounded ${
                                                        doc.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                    }`}>
                                                        {doc.status === 'success' ? 'Erfolg' : 'Fehler'}
                                                    </span>
                                                </div>

                                                {/* File actions */}
                                                {doc.files && Object.keys(doc.files).length > 0 && (
                                                    <div className="space-y-2">
                                                        {Object.entries(doc.files).map(([format, fileData]) => (
                                                            <div key={format} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                                                <div className="flex items-center gap-2">
                                                                    {format === 'pdf' ? (
                                                                        <FiFile className="text-red-600" size={18} />
                                                                    ) : (
                                                                        <FiFile className="text-blue-600" size={18} />
                                                                    )}
                                                                    <span className="text-sm font-medium uppercase">{format}</span>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    {format === 'pdf' && (
                                                                        <button
                                                                            onClick={() => handlePreviewPdf(fileData.download_url, doc.document_name + '.pdf')}
                                                                            className="flex items-center gap-1 px-3 py-1 text-sm text-mainBlue hover:text-blue-800 border border-mainBlue rounded hover:bg-blue-50"
                                                                        >
                                                                            <FiEye size={16} />
                                                                            Vorschau
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        onClick={() => handleDownload(doc.id, format)}
                                                                        className="flex items-center gap-1 px-3 py-1 text-sm bg-mainBlue text-white rounded hover:bg-opacity-90"
                                                                    >
                                                                        <FiDownload size={16} />
                                                                        Download
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Debug Section - Placeholder Mappings */}
                                                {doc.placeholder_data && Object.keys(doc.placeholder_data).length > 0 && (
                                                    <div className="mt-3 border-t border-gray-200 pt-3">
                                                        <button
                                                            onClick={() => toggleDebugSection(doc.id)}
                                                            className="flex items-center justify-between w-full p-2 bg-gray-100 hover:bg-gray-200 rounded transition"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <FiCode className="text-gray-600" size={16} />
                                                                <span className="text-sm font-semibold text-gray-700">
                                                                    Debug: Platzhalter-Zuordnung ({Object.keys(doc.placeholder_data).length})
                                                                </span>
                                                            </div>
                                                            {expandedDebugSections[doc.id] ? (
                                                                <FiChevronUp className="text-gray-600" size={18} />
                                                            ) : (
                                                                <FiChevronDown className="text-gray-600" size={18} />
                                                            )}
                                                        </button>

                                                        {expandedDebugSections[doc.id] && (
                                                            <div className="mt-2 p-3 bg-gray-900 rounded-lg max-h-96 overflow-y-auto">
                                                                <div className="space-y-1">
                                                                    {Object.entries(doc.placeholder_data).map(([placeholder, value], pIdx) => (
                                                                        <div key={pIdx} className="font-mono text-xs">
                                                                            <span className="text-yellow-400">{placeholder}</span>
                                                                            <span className="text-gray-400"> =&gt; </span>
                                                                            <span className="text-green-400">
                                                                                {value === null ? (
                                                                                    <span className="text-red-400">null</span>
                                                                                ) : value === '' ? (
                                                                                    <span className="text-orange-400">(leer)</span>
                                                                                ) : (
                                                                                    String(value)
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div className="mb-4 flex justify-center">
                                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                                            <FiAlertCircle className="text-red-600" size={32} />
                                        </div>
                                    </div>
                                    <p className="text-center text-gray-700 mb-4">
                                        Bei der Generierung sind Fehler aufgetreten.
                                    </p>

                                    {/* Show all collected error messages if available */}
                                    {generationResult.allErrorMessages && generationResult.allErrorMessages.length > 0 ? (
                                        <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
                                            <h4 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                                                <FiAlertCircle size={18} />
                                                Fehlerdetails ({generationResult.allErrorMessages.length}):
                                            </h4>
                                            <div className="space-y-2">
                                                {generationResult.allErrorMessages.map((error, idx) => (
                                                    <div key={idx} className="flex items-start gap-2 text-sm text-red-700 bg-white rounded p-2 border border-red-200">
                                                        <span className="text-red-500 font-bold flex-shrink-0">{idx + 1}.</span>
                                                        <span className="flex-1">{error}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Fallback to old error display format */}
                                            {generationResult.errors && Object.keys(generationResult.errors).length > 0 && (
                                                <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
                                                    <h4 className="font-semibold text-red-800 mb-2">Fehler:</h4>
                                                    <ul className="list-disc list-inside text-sm text-red-700">
                                                        {Object.values(generationResult.errors).flat().map((error, idx) => (
                                                            <li key={idx}>{error}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {generationResult.missing_placeholders && generationResult.missing_placeholders.length > 0 && (
                                                <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                                                    <h4 className="font-semibold text-yellow-800 mb-2">
                                                        Fehlende Platzhalter:
                                                    </h4>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        {generationResult.missing_placeholders.map((ph, idx) => (
                                                            <div key={idx} className="text-xs font-mono bg-white px-2 py-1 rounded border border-yellow-300">
                                                                {ph}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {/* Action guidance section */}
                                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded p-4">
                                        <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                                            <FiAlertCircle size={16} />
                                            Lösungsvorschläge:
                                        </h4>
                                        <ul className="text-sm text-blue-700 space-y-1">
                                            {generationResult.missing_placeholders && generationResult.missing_placeholders.length > 0 && (
                                                <li>• Überprüfen Sie die Platzhalter-Konfiguration in der Platzhalter-Verwaltung</li>
                                            )}
                                            <li>• Stellen Sie sicher, dass alle erforderlichen Kundendaten in der Datenbank vorhanden sind</li>
                                            <li>• Überprüfen Sie die Datenbankverbindungen in der Platzhalter-Konfiguration</li>
                                            <li>• Kontaktieren Sie den Administrator, wenn das Problem weiterhin besteht</li>
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="border-t border-gray-200 p-6 flex justify-end">
                            <button
                                onClick={handleCloseResultModal}
                                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                Schließen
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* PDF Preview Modal */}
            <PdfPreviewModal
                isOpen={showPdfPreview}
                onClose={handleClosePdfPreview}
                pdfUrl={previewPdfUrl}
                fileName={previewFileName}
            />
        </div>
    );
};

export default DocumentGeneration;
